// FILE: src/utils/firebase/cache/transactionsCached.js
// Canonical Transactions Cache (IndexedDB "pp-cache", store "txByQuery")

import { TransactionsAPI } from "..";
import { LOCAL_DB } from "../core/collections";

/* ---------- Debug toggle ---------- */
const DEBUG = String(process.env.REACT_APP_DEBUG_TXCACHE || "") === "1";
const dbg = (...args) => { if (DEBUG) console.debug("[TxCache]", ...args); };

const DB_NAME = LOCAL_DB.name;
const DB_VERSION = LOCAL_DB.version;
const STORE_TX = LOCAL_DB.stores.txByQuery;

/* ---------- IndexedDB helpers ---------- */
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_TX)) {
        db.createObjectStore(STORE_TX, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function withStore(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_TX, mode);
    const store = tx.objectStore(STORE_TX);
    let done = false;
    tx.oncomplete = () => !done && resolve();
    tx.onerror = () => !done && reject(tx.error);
    const res = fn(store);
    if (res instanceof Promise) {
      res.then((v) => { done = true; resolve(v); })
         .catch((e) => { done = true; reject(e); });
    }
  });
}

/* ---------- Key Builder ---------- */
export function buildTxCacheKey({ userEmail, fromDate, toDate, latestFirst = true }) {
  const email = String(userEmail || "").toLowerCase();
  const f = fromDate ? String(fromDate) : "";
  const t = toDate ? String(toDate) : "";
  const sort = latestFirst ? "desc" : "asc";
  return `tx:${email}|${f}|${t}|${sort}|v2`;
}

/* ---------- Store ops ---------- */
export async function getTxCache(key) {
  return withStore("readonly", (store) => new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  }));
}

export async function writeTxCache({ key, rows, columns, meta }) {
  const payload = {
    key,
    rows,
    columns,
    meta: { savedAt: Date.now(), ...(meta || {}) },
  };
  return withStore("readwrite", (store) => { store.put(payload); });
}

export async function clearTxCache(key) {
  return withStore("readwrite", (store) => { store.delete(key); });
}

/* ---------- TTL + utils ---------- */
const TXCACHE_TTL = Number(process.env.REACT_APP_TX_CACHE_TTL_MS) || 60 * 60 * 1000; // 1h default

function inferColumns(items) {
  const cols = [];
  const seen = new Set();
  for (const row of items || []) {
    if (row && typeof row === "object") {
      for (const k of Object.keys(row)) {
        if (!seen.has(k)) { seen.add(k); cols.push(k); }
      }
    }
  }
  return cols;
}

function getUpdatedAtMs(row) {
  const t = row?.updatedAt ?? row?.createdAt;
  if (t && typeof t.toMillis === "function") return t.toMillis();
  if (t instanceof Date) return t.getTime();
  const parsed = Date.parse(t);
  return Number.isFinite(parsed) ? parsed : 0;
}

function maxUpdatedAtMs(rows = []) {
  let max = 0;
  for (const r of rows) {
    const m = getUpdatedAtMs(r);
    if (m > max) max = m;
  }
  return max;
}

function upsertRowsById(existing = [], incoming = [], latestFirst = true) {
  const map = new Map(existing.map((r) => [r.id, r]));
  for (const r of incoming || []) {
    if (!r?.id) continue;
    map.set(r.id, r);
  }
  const merged = Array.from(map.values());
  merged.sort((a, b) => {
    const da = Date.parse(String(a?.date || ""));
    const db = Date.parse(String(b?.date || ""));
    if (Number.isFinite(da) && Number.isFinite(db) && da !== db) {
      return latestFirst ? (db - da) : (da - db);
    }
    const ua = getUpdatedAtMs(a);
    const ub = getUpdatedAtMs(b);
    return latestFirst ? (ub - ua) : (ua - ub);
  });
  return merged;
}

/* ---------- Main API ---------- */
export async function getUserTransactionsCached({
  userEmail,
  fromDate,
  toDate,
  latestFirst = true,
  cap = 9999,
  ttlMs = TXCACHE_TTL,
  bypassCache = false,
} = {}) {
  const key = buildTxCacheKey({ userEmail, fromDate, toDate, latestFirst });
  dbg("get", { key, userEmail, fromDate, toDate, latestFirst, cap, ttlMs, bypassCache });

  const tryDeltaRefresh = async (cached) => {
    const sinceMs = cached?.meta?.lastFetchedAt ?? 0;
    if (!sinceMs) { dbg("delta-skip-no-since", { key }); return null; }

    const delta = await TransactionsAPI.fetchUserTransactionsDelta({
      userEmail,
      sinceUpdatedAt: sinceMs,
      latestFirst,
      cap,
    });

    if (!delta || delta.length === 0) {
      dbg("delta-empty-use-cached", { key, sinceMs });
      await writeTxCache({
        key,
        rows: cached.rows,
        columns: cached.columns,
        meta: { lastFetchedAt: sinceMs },
      });
      return { rows: cached.rows, columns: cached.columns, fromCache: true };
    }

    const merged = upsertRowsById(cached.rows || [], delta, latestFirst);
    const columns = inferColumns(merged);
    const newMax = Math.max(sinceMs, maxUpdatedAtMs(delta));
    dbg("delta-merged", { key, delta: delta.length, merged: merged.length, sinceMs, newMax });

    await writeTxCache({
      key,
      rows: merged,
      columns,
      meta: { lastFetchedAt: newMax },
    });

    return { rows: merged, columns, fromCache: false };
  };

  if (!bypassCache) {
    const cached = await getTxCache(key);
    const freshByTTL = cached?.meta?.savedAt && (Date.now() - cached.meta.savedAt) <= ttlMs;

    if (cached && Array.isArray(cached.rows)) {
      if (freshByTTL) {
        dbg("hit-ttl", { key, rows: cached.rows.length });
        return { rows: cached.rows, columns: cached.columns || [], fromCache: true };
      }
      const deltaResult = await tryDeltaRefresh(cached);
      if (deltaResult) return deltaResult;
    }
  }

  dbg("full-fetch", { key });
  const rows = await TransactionsAPI.fetchUserTransactionsRange({
    userEmail,
    fromDate,
    toDate,
    latestFirst,
    cap,
  });

  const columns = inferColumns(rows);
  await writeTxCache({
    key,
    rows,
    columns,
    meta: { lastFetchedAt: maxUpdatedAtMs(rows) },
  });

  return { rows, columns, fromCache: false };
}

/* ---------- Write-through helpers ---------- */
export async function patchTxCacheAfterAdd({ userEmail, tx }) {
  const key = buildTxCacheKey({ userEmail, latestFirst: true });
  const cached = await getTxCache(key);
  if (cached?.rows) {
    const updated = tx?.id ? [tx, ...cached.rows.filter(r => r.id !== tx.id)] : [tx, ...cached.rows];
    const columns = inferColumns(updated);
    const updatedMs = getUpdatedAtMs(tx) || Date.now();
    const lastFetchedAt = Math.max(cached?.meta?.lastFetchedAt ?? 0, updatedMs);
    dbg("patch-add", { key, id: tx?.id, rows: updated.length, lastFetchedAt });
    await writeTxCache({ key, rows: updated, columns, meta: { lastFetchedAt, patched: true } });
  } else {
    dbg("patch-add-miss", { key, id: tx?.id });
  }
}

export async function patchTxCacheAfterUpdate({ userEmail, tx }) {
  const key = buildTxCacheKey({ userEmail, latestFirst: true });
  const cached = await getTxCache(key);
  if (cached?.rows) {
    const updated = cached.rows.map((r) => (r.id === tx.id ? tx : r));
    const columns = inferColumns(updated);
    const updatedMs = getUpdatedAtMs(tx) || Date.now();
    const lastFetchedAt = Math.max(cached?.meta?.lastFetchedAt ?? 0, updatedMs);
    dbg("patch-update", { key, id: tx?.id, lastFetchedAt });
    await writeTxCache({ key, rows: updated, columns, meta: { lastFetchedAt, patched: true } });
  } else {
    dbg("patch-update-miss", { key, id: tx?.id });
  }
}

export async function patchTxCacheAfterDelete({ userEmail, ids }) {
  const key = buildTxCacheKey({ userEmail, latestFirst: true });
  const cached = await getTxCache(key);
  if (cached?.rows) {
    const updated = cached.rows.filter((r) => !ids.includes(r.id));
    const columns = inferColumns(updated);
    dbg("patch-delete", { key, ids, rows: updated.length });
    await writeTxCache({
      key,
      rows: updated,
      columns,
      meta: { lastFetchedAt: cached?.meta?.lastFetchedAt ?? 0, patched: true },
    });
  } else {
    dbg("patch-delete-miss", { key, ids });
  }
}

/** Efficient bulk add patch, called AFTER a successful batch commit */
export async function patchTxCacheAfterBulkAdd({ userEmail, txs = [] }) {
  if (!Array.isArray(txs) || txs.length === 0) return;

  const key = buildTxCacheKey({ userEmail, latestFirst: true });
  const cached = await getTxCache(key);
  if (!cached?.rows) { dbg("patch-bulk-miss", { key, count: txs.length }); return; }

  const map = new Map(cached.rows.map(r => [r.id, r]));
  for (const tx of txs) {
    if (!tx?.id) continue;
    map.set(tx.id, tx);
  }
  const merged = Array.from(map.values());
  merged.sort((a, b) => {
    const da = Date.parse(String(a?.date || "")); const db = Date.parse(String(b?.date || ""));
    if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return db - da;
    const ua = getUpdatedAtMs(a); const ub = getUpdatedAtMs(b);
    return ub - ua;
  });

  const columns = inferColumns(merged);
  const lastFetchedAt = Math.max(
    cached?.meta?.lastFetchedAt ?? 0,
    ...txs.map(t => getUpdatedAtMs(t) || Date.now())
  );

  dbg("patch-bulk", { key, added: txs.length, rows: merged.length, lastFetchedAt });

  await writeTxCache({
    key,
    rows: merged,
    columns,
    meta: { lastFetchedAt, patched: true },
  });
}

export async function clearAllTxCachesForUser(userEmail) {
  const prefix = `tx:${String(userEmail || "").toLowerCase()}|`;
  return withStore("readwrite", (store) => new Promise((resolve, reject) => {
    const req = store.openCursor();
    req.onerror = () => reject(req.error);
    req.onsuccess = (ev) => {
      const cursor = ev.target.result;
      if (!cursor) { dbg("clear-all-complete", { prefix }); return resolve(true); }
      const rec = cursor.value;
      if (rec?.key?.startsWith(prefix)) {
        cursor.delete();
      }
      cursor.continue();
    };
  }));
}
